import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, ShieldCheck, Lock, Send, Loader2, MessageSquarePlus } from "lucide-react";

const PROMPTS = [
  "Welche Verträge laufen in den nächsten 60 Tagen aus?",
  "Welche Kunden wurden noch nicht kontaktiert?",
  "Fasse die Vertragshistorie dieses Kunden zusammen.",
  "Formuliere eine Verlängerungsnachricht auf Deutsch.",
];

export default function AIAssistant() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [prompt, setPrompt] = useState("");
  const [sending, setSending] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);

  const { data: cfg } = useQuery({ queryKey: ["ai-config"], queryFn: async () => (await api.get("/ai/config")).data });
  const { data: insights } = useQuery({ queryKey: ["ai-insights"], queryFn: async () => (await api.get("/ai/insights")).data });
  const { data: feedback } = useQuery({ queryKey: ["ai-feedback"], queryFn: async () => (await api.get("/ai/feedback")).data });

  const mode = cfg?.ai?.mode || "not_configured";
  const configured = mode === "private" && cfg?.ai?.endpoint;

  const ask = async () => {
    if (!prompt.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/ai/ask", { prompt });
      toast.success("Antwort erhalten");
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader title="KI-Assistent"
        description="Privater KI-Assistent — Fakten stammen immer aus deterministischen Backend-Abfragen"
        actions={<Badge variant="outline" className="gap-1.5" data-testid="ai-mode-badge">
          <span className={`h-2 w-2 rounded-full ${configured ? "bg-emerald-500" : "bg-amber-500"}`} />
          {configured ? "Privater Modus aktiv" : "Nicht konfiguriert"}
        </Badge>} />

      {!configured && (
        <Alert className="mb-4 border-amber-500/30 bg-amber-500/5">
          <Lock className="h-4 w-4" />
          <AlertTitle>KI-Inferenz nicht konfiguriert</AlertTitle>
          <AlertDescription>
            Es wird <strong>kein externer KI-Anbieter</strong> kontaktiert. Verbinden Sie einen privaten, lokal gehosteten
            Modell-Endpunkt in den Einstellungen, um Antworten zu erhalten. Die App weicht niemals stillschweigend auf eine Cloud-KI aus.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionCard title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" />Assistent</span>}>
            <div className="mb-3 flex flex-wrap gap-2">
              {PROMPTS.map((p) => (
                <button key={p} onClick={() => setPrompt(p)} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:border-accent/40 hover:text-foreground" data-testid="ai-quick-prompt">
                  {p}
                </button>
              ))}
            </div>
            <Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Stellen Sie eine Frage zu Kunden, Verträgen oder Verlängerungen…" data-testid="ai-prompt-input" />
            <div className="mt-3 flex justify-end">
              {can(user?.role, "ai:use") && (
                <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={ask} disabled={sending} data-testid="ai-submit-button">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Senden
                </Button>
              )}
            </div>
          </SectionCard>
        </div>

        <SectionCard title={<span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" />Faktenbasierte Einblicke</span>}>
          <div className="space-y-2">
            {(insights?.insights || []).map((i) => (
              <div key={i.label} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <span className="text-sm text-muted-foreground">{i.label}</span>
                <span className="text-lg font-semibold">{i.value}</span>
              </div>
            ))}
            <p className="pt-2 text-[11px] text-muted-foreground">Diese Zahlen kommen direkt aus Ihren Mandantendaten, nicht vom Sprachmodell.</p>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Feedback & kontrolliertes Lernen" className="mt-4"
        action={can(user?.role, "ai:feedback") && <Button variant="outline" size="sm" onClick={() => setFbOpen(true)} data-testid="add-feedback-button"><MessageSquarePlus className="mr-1.5 h-4 w-4" />Korrektur erfassen</Button>}>
        <div className="space-y-2">
          {(feedback?.items || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Noch keine Korrekturen erfasst. Validierte Korrekturen verbessern Extraktionsregeln und Prompt-Vorlagen — niemals automatisches Nachtrainieren.</p>}
          {(feedback?.items || []).map((f) => (
            <div key={f.id} className="rounded-md border border-border px-4 py-3 text-sm">
              <div className="flex items-center justify-between"><Badge variant="secondary">{f.validation_status}</Badge><span className="text-xs text-muted-foreground">{f.created_by_name}</span></div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div><span className="text-[11px] uppercase text-muted-foreground">Original</span><p className="text-muted-foreground line-through">{f.original_output}</p></div>
                <div><span className="text-[11px] uppercase text-muted-foreground">Korrektur</span><p>{f.corrected_output}</p></div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <FeedbackDialog open={fbOpen} onOpenChange={setFbOpen} onSaved={() => qc.invalidateQueries({ queryKey: ["ai-feedback"] })} />
    </div>
  );
}

function FeedbackDialog({ open, onOpenChange, onSaved }) {
  const [orig, setOrig] = useState("");
  const [corr, setCorr] = useState("");
  const [docType, setDocType] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/ai/feedback", { original_output: orig, corrected_output: corr, doc_type: docType, validation_status: "validated" });
      toast.success("Feedback gespeichert"); onSaved(); onOpenChange(false); setOrig(""); setCorr(""); setDocType("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Korrektur erfassen</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Dokument-/Aufgabentyp</Label><Input value={docType} onChange={(e) => setDocType(e.target.value)} placeholder="z. B. Mobilfunkvertrag" /></div>
          <div className="space-y-1.5"><Label>Ursprüngliche Ausgabe</Label><Textarea rows={2} value={orig} onChange={(e) => setOrig(e.target.value)} data-testid="feedback-original-input" /></div>
          <div className="space-y-1.5"><Label>Korrigierte Ausgabe</Label><Textarea rows={2} value={corr} onChange={(e) => setCorr(e.target.value)} data-testid="feedback-corrected-input" /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" disabled={busy} data-testid="feedback-save-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
